import { Component } from '@theme/component';
import { getIOSVersion } from '@theme/utilities';
import { Slideshow } from '@theme/slideshow';
import { ThemeEvents, SlideshowSelectEvent } from '@theme/events';
import { IbdEvents, IbdCylindoModelSelectedEvent } from '@theme/_ibd-events';

/** @typedef {import('@theme/events').VariantUpdateEvent} VariantUpdateEvent */
/** @typedef {import('@theme/events').VariantSelectedEvent} VariantSelectedEvent */
/** @typedef {import('@theme/_ibd-events').IbdCylindoViewerSettings} Settings */
/** @typedef {import('@theme/_ibd-events').IbdProductUpdateEvent} IbdProductUpdateEvent */
/** @typedef {import('@theme/_ibd-cylindo-events').CylindoViewerFullscreenChangedEvent} CylindoViewerFullscreenChangedEvent */

/** @type {Settings} */
const defaultSettings = {
  code: '',
  configuration: '',
  enable_idle_animation: true,
  idle_animation_delay: 10,
};

class IbdCylindoSlideAnchor extends Component {
  /** @type {HTMLButtonElement|undefined} */
  #anchorButton;

  /** @type {HTMLButtonElement|undefined} */
  #returnButton;

  /** @type {number|undefined} */
  #cylindoSlideIndex;

  /** @type {number} */
  #previousSlideIndex = 0;

  /** @type {Slideshow|undefined} */
  #slideshowComponent;

  constructor() {
    super();
  }

  /**
   * Checks if an index is valid.
   * @param {any} index
   * @returns {boolean}
   */
  #isValidIndex(index) {
    return index !== null && index !== undefined && Number.isInteger(index) && index >= 0;
  }

  /**
   * Handles the click event.
   * @param {MouseEvent} event
   */
  #handleAnchorClick(event) {
    event.preventDefault();

    if (!this.#slideshowComponent) {
      console.error('IBD | CylindoSlideAnchor | #handleAnchorClick | No slideshow component found');
      this.#hide();
      return;
    }

    if (this.#cylindoSlideIndex === undefined || !this.#isValidIndex(this.#cylindoSlideIndex)) {
      console.error('IBD | CylindoSlideAnchor | #handleAnchorClick | Invalid slide index');
      this.#hide();
      return;
    }

    this.#slideshowComponent.select(this.#cylindoSlideIndex, event, { animate: true });
  }

  /**
   * Handles the return click event.
   * @param {MouseEvent} event
   */
  #handleReturnClick(event) {
    event.preventDefault();

    if (!this.#slideshowComponent) {
      console.error('IBD | CylindoSlideAnchor | #handleReturnClick | No slideshow component found');
      this.#hide();
      return;
    }

    this.#slideshowComponent.select(this.#previousSlideIndex, event, { animate: true });
  }

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect(event) {
    if (!this.#anchorButton) {
      console.error('IBD | CylindoSlideAnchor | #handleSlideshowSelect | No button found');
      return;
    }

    if (!this.#isValidIndex(event.detail.index)) {
      console.error('IBD | CylindoSlideAnchor | #handleSlideshowSelect | Invalid slide index');
      return;
    }

    if (event.detail.index === this.#cylindoSlideIndex) {
      this.classList.add('ibd-cylindo-slide-anchor--is-active');
    } else {
      this.classList.remove('ibd-cylindo-slide-anchor--is-active');
    }

    this.#previousSlideIndex = event.detail.previousIndex;
  }

  #hide() {
    this.hidden = true;
  }

  connectedCallback() {
    super.connectedCallback();

    const anchorButton = this.querySelector('[data-anchor-button]');
    if (!(anchorButton instanceof HTMLButtonElement)) {
      console.error('IBD | CylindoSlideAnchor | #connectedCallback | No button found');
      this.#hide();
      return;
    }

    const returnButton = this.querySelector('[data-return-button]');
    if (!(returnButton instanceof HTMLButtonElement)) {
      console.error('IBD | CylindoSlideAnchor | #connectedCallback | No button found');
      this.#hide();
      return;
    }

    const slideshowComponent = this.closest('slideshow-component');
    if (!(slideshowComponent instanceof Slideshow)) {
      console.error('IBD | CylindoSlideAnchor | #connectedCallback | No slideshow component found');
      this.#hide();
      return;
    }

    const cylindoSlideIndex = parseInt(this.dataset.slideIndex ?? '0', 10);
    if (!this.#isValidIndex(cylindoSlideIndex)) {
      console.error('IBD | CylindoSlideAnchor | #connectedCallback | Invalid slide index');
      this.#hide();
      return;
    }

    this.#anchorButton = anchorButton;
    this.#returnButton = returnButton;
    this.#slideshowComponent = slideshowComponent;
    this.#cylindoSlideIndex = cylindoSlideIndex;

    this.#anchorButton.addEventListener('click', this.#handleAnchorClick.bind(this));
    this.#returnButton.addEventListener('click', this.#handleReturnClick.bind(this));
    document.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect.bind(this));
  }
}

if (!customElements.get('ibd-cylindo-slide-anchor')) {
  customElements.define('ibd-cylindo-slide-anchor', IbdCylindoSlideAnchor);
}

/**
 * A custom element that manages the Cylindo viewer.
 * @extends {Component}
 */
class IbdCylindoViewer extends Component {
  /** @type {any|undefined} */
  viewer;

  /** @type {HTMLSelectElement|undefined} */
  modelSelect;

  /** @type {Settings} */
  settings = defaultSettings;

  /** @type {MutationObserver|null} */
  settingsObserver = null;

  /** @type {boolean} */
  userHasInteracted = false;

  /** @type {number|null} */
  animationLoop = null;

  /** @type {((event: Event) => void)|null} */
  #boundStartIdleAnimation = null;

  /** @type {((event: Event) => void)|null} */
  #boundStopIdleAnimation = null;

  /** @type {((event: Event) => void)|null} */
  #boundHandleModelSelect = null;

  /** @type {((event: CylindoViewerFullscreenChangedEvent) => void)|null} */
  #boundHandleFullscreenChange = null;

  /** @type {((event: IbdCylindoModelSelectedEvent) => void)|null} */
  #boundHandleModelSelected = null;

  /** @type {((event: VariantUpdateEvent | IbdProductUpdateEvent) => void)|null} */
  #boundHandleVariantUpdate = null;

  constructor() {
    super();
  }

  disconnectedCallback() {
    this.#stopIdleAnimation();
    this.#unbindEventListeners();
  }

  connectedCallback() {
    super.connectedCallback();

    const viewer = this.querySelector('cylindo-viewer');
    if (!viewer) {
      console.error('IBD | Cylindo | #connectedCallback | No viewer found');
      return;
    }

    this.viewer = viewer;

    try {
      const settings = JSON.parse(this.dataset.settings ?? '{}');
      this.settings = { ...defaultSettings, ...settings };
    } catch (error) {
      console.error('IBD | Cylindo | #connectedCallback | Error parsing settings');
      console.error(error);
    }

    const modelSelect = this.querySelector('[data-cylindo-model-select]');
    if (modelSelect instanceof HTMLSelectElement) {
      this.modelSelect = modelSelect;
    }

    document.addEventListener('DOMContentLoaded', this.#bindEventListeners.bind(this));
  }

  /**
   * Handles the event when the user has selected a new model.
   * @param {Event} event
   */
  #handleModelSelect(event) {
    if (!this.viewer) {
      console.error('IBD | Cylindo | #handleModelSelect | No viewer found');
      return;
    }

    if (!(event.target instanceof HTMLSelectElement)) {
      console.error('IBD | Cylindo | #handleModelSelect | No valid target found');
      return;
    }

    this.dispatchEvent(new IbdCylindoModelSelectedEvent(event.target.value));
  }

  /**
   * Handles the event when a new model has been selected.
   * @param {IbdCylindoModelSelectedEvent} event
   * @returns
   */
  #handleModelSelected(event) {
    if (!event.detail.code || event.detail.code === this.settings.code) return;
    this.viewer?.setAttribute('code', event.detail.code);
  }

  /**
   * Handles a variant update event.
   *
   * @param {VariantUpdateEvent | IbdProductUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate(event) {
    if (!event.detail.data || !event.detail.data.html) {
      console.error('IBD | Cylindo Viewer | #handleVariantUpdate | No data or HTML found in event detail');
      return;
    }

    /** @type {HTMLElement|null} */
    const newElement = event.detail.data.html.querySelector('ibd-cylindo-viewer');
    if (!newElement) {
      console.error('IBD | Cylindo Viewer | #handleVariantUpdate | Cylindo viewer element not found in html data');
      return;
    }

    try {
      const newSettings = JSON.parse(newElement.dataset.settings ?? '{}');

      if (newSettings.configuration !== this.settings.configuration) {
        this.viewer?.setAttribute('configuration', newSettings.configuration);
      }

      if (newSettings.code !== this.settings.code) {
        this.viewer?.setAttribute('code', newSettings.code);
        this.viewer.code = newSettings.code;
      }

      this.settings = { ...this.settings, ...newSettings };
    } catch (error) {
      console.error('IBD | Cylindo Viewer | #handleVariantUpdate', error);
    }
  }

  /**
   * Handles the fullscreen change event.
   *
   * This is a workaround for the lack of a comprehensive Full Screen API in iOS.
   * In iOS, the Cylindo Viewer will manage the fullscreen by changing its z-index,
   * with the help of the --z-index-ios-fullscreen custom property.
   *
   * However, our header also has a z-index --layer-sticky (which is around 8).
   * For some reason, even when the --z-index-ios-fullscreen is above --layer-sticky,
   * our header will still be on top of the viewer. Maybe due to Cylindo's shadow-root?
   *
   * So we need to temporarily unset the z-index of the header when the viewer is fullscreen.
   *
   * Read more: https://pdp.cylindo.com/API/cylindo-viewer#--z-index-ios-fullscreen
   * @param {CylindoViewerFullscreenChangedEvent} event
   */
  #handleFullscreenChange(event) {
    if (!this.viewer || typeof event.detail !== 'boolean' || !getIOSVersion()) return;

    const header = document.querySelector('#header-group header');
    if (!(header instanceof HTMLElement)) return;

    const isFullscreen = event.detail;
    if (isFullscreen) header.style.setProperty('z-index', 'unset');
    else header.style.removeProperty('z-index');
  }

  #startIdleAnimation() {
    if (this.userHasInteracted) return;

    const animateToNextFrame = () => {
      if (!this.viewer) return;

      if (this.userHasInteracted) {
        this.#stopIdleAnimation();
        return;
      }

      this.viewer.animateToFrame(this.viewer.frame - 4);
      this.viewer.addEventListener(
        'frame-change',
        () => {
          setTimeout(() => {
            this.viewer.animateToFrame(this.viewer.frame + 5);
            setTimeout(() => {
              this.viewer.animateToFrame(this.viewer.frame - 1);
            }, 700);
          }, 600);
        },
        { once: true }
      );

      this.animationLoop = setTimeout(() => animateToNextFrame(), this.settings.idle_animation_delay * 1000);
    };

    setTimeout(() => animateToNextFrame(), this.settings.idle_animation_delay * 1000);
  }

  #stopIdleAnimation() {
    if (!this.animationLoop) return;
    clearTimeout(this.animationLoop);
    this.animationLoop = null;
  }

  #bindEventListeners() {
    if (this.viewer?.loaded && !this.userHasInteracted) {
      this.#startIdleAnimation();
    } else {
      this.#boundStartIdleAnimation = () => this.#startIdleAnimation();
      this.viewer?.addEventListener('ready', this.#boundStartIdleAnimation, { once: true });

      this.#boundStopIdleAnimation = () => this.#stopIdleAnimation();
      this.viewer?.addEventListener('spin-start', this.#boundStopIdleAnimation, { once: true });
      this.viewer?.addEventListener('zoom-change', this.#boundStopIdleAnimation, { once: true });
    }

    this.#boundHandleFullscreenChange = (event) => this.#handleFullscreenChange(event);
    this.viewer?.addEventListener('fullscreen-change', this.#boundHandleFullscreenChange);

    this.#boundHandleModelSelect = (event) => this.#handleModelSelect(event);
    this.modelSelect?.addEventListener('change', this.#boundHandleModelSelect);

    this.#boundHandleModelSelected = (event) => this.#handleModelSelected(event);
    this.addEventListener(IbdEvents.ibdCylindoModelSelected, this.#boundHandleModelSelected);

    this.#boundHandleVariantUpdate = (event) => this.#handleVariantUpdate(event);
    document.addEventListener(ThemeEvents.variantUpdate, this.#boundHandleVariantUpdate);
    document.addEventListener(IbdEvents.ibdProductUpdated, this.#boundHandleVariantUpdate);
  }

  #unbindEventListeners() {
    if (this.#boundStartIdleAnimation) {
      this.viewer?.removeEventListener('ready', this.#boundStartIdleAnimation);
      this.#boundStartIdleAnimation = null;
    }

    if (this.#boundStopIdleAnimation) {
      this.viewer?.removeEventListener('spin-start', this.#boundStopIdleAnimation);
      this.viewer?.removeEventListener('zoom-change', this.#boundStopIdleAnimation);
      this.#boundStopIdleAnimation = null;
    }

    if (this.#boundHandleFullscreenChange) {
      this.viewer?.removeEventListener('fullscreen-change', this.#boundHandleFullscreenChange);
      this.#boundHandleFullscreenChange = null;
    }

    if (this.#boundHandleModelSelect) {
      this.modelSelect?.removeEventListener('change', this.#boundHandleModelSelect);
      this.#boundHandleModelSelect = null;
    }

    if (this.#boundHandleModelSelected) {
      this.removeEventListener(IbdEvents.ibdCylindoModelSelected, this.#boundHandleModelSelected);
      this.#boundHandleModelSelected = null;
    }

    if (this.#boundHandleVariantUpdate) {
      document.removeEventListener(ThemeEvents.variantUpdate, this.#boundHandleVariantUpdate);
      document.removeEventListener(IbdEvents.ibdProductUpdated, this.#boundHandleVariantUpdate);
      this.#boundHandleVariantUpdate = null;
    }
  }
}

if (!customElements.get('ibd-cylindo-viewer')) {
  customElements.define('ibd-cylindo-viewer', IbdCylindoViewer);
}
